import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import {Col, FormFeedback, Input, Label, Modal, ModalBody, ModalHeader, Row} from 'reactstrap';
import {useDispatch, useSelector} from "react-redux";
import {deployedLambdaDetails, deployLambda, getAvailableRoles} from "../../store/daemon/actions";
import Select from "react-select";
import {customStyles} from "../../helpers/component_helpers";

const DeployDetailsModal = ({isOpen, toggle, masterLambdaId, setSubmitted, defaultDeploymentOption, roleArn, lambdaArn }) => {
  const deploymentType = ["Automatic", "Manual"];
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState({
    deploymentType: deploymentType[defaultDeploymentOption],
    lambdaArn: lambdaArn ? lambdaArn : '',
    roleArn: roleArn ? roleArn : ''
  })
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const {daemon_details, available_roles} = useSelector(state => ({
    daemon_details: state.Daemon.daemon_detail,
    available_roles: state.Daemon.available_roles,
  }));

  function handleToggle(){
    setFormValues({
      deploymentType: '',
      lambdaArn: '',
      roleArn: ''
    });
    setErrors({});
    setTouched({});
    toggle();
  }

  useEffect(() => {
    setFormValues({
      deploymentType: deploymentType[defaultDeploymentOption],
      lambdaArn: lambdaArn ? lambdaArn : '',
      roleArn: roleArn ? roleArn : ''
    })
  }, [defaultDeploymentOption, roleArn, lambdaArn])

  useEffect(() => {
    dispatch(getAvailableRoles())
  }, [])

  const validateField = (name, value) => {
    let newError = {};
    if (name === 'roleArn' && !value) {
      newError[name] = "Role ARN cannot be empty";
    } else if (name === 'lambdaArn' && !value) {
      newError[name] = "Lambda ARN cannot be empty";
    } else {
      newError[name] = null;
    }
    return newError;
  };

  const deploymentTypeOptions = deploymentType.map(deploymentType => {
    return {
      label: deploymentType, value: deploymentType,
    }
  })

  const handleChange = (event) => {
    const {name, value} = event.target;

    setFormValues(prevValues => ({...prevValues, [name]: value}));

    setErrors(prevErrors => ({...prevErrors, ...validateField(name, value)}));
  };

  const handleBlur = (event) => {
    const {name, value} = event.target;
    setTouched(prev => ({...prev, [name]: true}));
    setErrors(prevErrors => ({...prevErrors, ...validateField(name, value)}));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let newErrors = validateField("roleArn", formValues.roleArn);
    if (formValues.deploymentType === deploymentType[1]) {
      newErrors = {...newErrors, ...validateField("lamdbaArn", formValues.lambdaArn)}
    }
    setErrors(newErrors);

    if (Object.values(newErrors).some(err => err)) return;

    setSubmitted(true);
    if(!daemon_details) return;
    if (formValues.deploymentType === deploymentType[0]) {
      const payload = {
        role_arn: formValues.roleArn,
        master_lambda_id: masterLambdaId,
        monitored_resource_id: daemon_details.monitored_resource_id,
      };
      dispatch(deployLambda(payload));
    } else {
      const payload = {
        role_arn: formValues.roleArn,
        master_lambda_id: masterLambdaId,
        lambda_arn: formValues.lambdaArn,
        monitored_resource_id: daemon_details.monitored_resource_id
      }
      dispatch(deployedLambdaDetails(payload));
    }
    handleToggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      role="dialog"
      autoFocus
      centered
      className="exampleModal"
      tabIndex="-1"
    >
      <div className="modal-content">
        <ModalHeader toggle={handleToggle}>Role for Executing Lambda</ModalHeader>
        <ModalBody>
          <div className="d-flex flex-column">
            <Row>
              <Col md={6} className="mb-3 mt-2">
                <Label>Deployment Type</Label>
                <Select
                  id="deploymentType"
                  name="deploymentType"
                  options={deploymentTypeOptions}
                  className="select2-selection bg-transparent text-white"
                  onBlur={() => {
                    handleBlur({
                      target: {
                        name: 'deploymentType',
                        value: formValues.deploymentType
                      }
                    });
                  }}
                  value={deploymentTypeOptions.find((option) => option.label === formValues.deploymentType)}
                  onChange={(e) => {
                    e['name'] = 'deploymentType';
                    const payload = {
                      target: e
                    }
                    handleChange(payload);
                  }}
                  theme={(theme) => ({
                    ...theme, borderRadius: 0, colors: {
                      ...theme.colors,
                      neutral0: '#222736',
                      neutral5: '#485EC4',
                      neutral10: '#485EC4',
                      neutral60: '#ffffff',
                      neutral70: '#ffffff',
                      neutral80: '#ffffff',
                      neutral90: '#ffffff',
                      primary: '#485EC4',
                    },
                  })}
                  styles={customStyles}
                />
                {touched.deploymentType && errors.deploymentType && (
                  <FormFeedback type="invalid">{errors.deploymentType}</FormFeedback>)}
              </Col>
              <Col md={6} className="mb-3 mt-2">
              </Col>
              <Col md={12} className="mb-3 mt-2">
                <Label htmlFor="roleArn mb-1">Role ARN(Role to be attached to the Lambda)</Label>
                <Input
                  id="roleArn"
                  name="roleArn"
                  type="text"
                  value={formValues.roleArn}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={touched.roleArn && errors.roleArn}
                  placeholder="arn:aws:service:region:account-id:resource-type/resource-id"
                />
                {touched.roleArn && errors.roleArn && (<FormFeedback type="invalid">{errors.roleArn}</FormFeedback>)}
              </Col>
              {formValues.deploymentType === deploymentType[1] ?
                <Col md={12} className="mb-3 mt-2">
                  <Label htmlFor="lambdaArn mb-1">Deployed Lambda ARN</Label>
                  <Input
                    id="lambdaArn"
                    name="lambdaArn"
                    type="text"
                    value={formValues.lambdaArn}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.lambdaArn && errors.lambdaArn}
                    placeholder="arn:aws:service:region:account-id:resource-type/resource-id"
                  />
                  {touched.lambdaArn && errors.lambdaArn && (
                    <FormFeedback type="invalid">{errors.lambdaArn}</FormFeedback>)}
                </Col>
                : null
              }
            </Row>
            <button
              type="submit"
              className="btn btn-primary"
              onClick={handleSubmit}>
              Deploy Lambda
            </button>
          </div>
        </ModalBody>
      </div>
    </Modal>);
};

DeployDetailsModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  masterLambdaId: PropTypes.string.isRequired,
  setSubmitted: PropTypes.func.isRequired,
  defaultDeploymentOption: PropTypes.number.isRequired,
  roleArn: PropTypes.string,
  lambdaArn: PropTypes.string,
};

export default DeployDetailsModal;